<template>
	<div>
		<section class="c-document-search__form">
			<div class="c-document-search__inner">
				<DsText v-if="vm.CurrentBlock.Header" html-element="h2" text-style="medium" text-type="heading-2xl"
					id="tabledescription">
					{{ vm.CurrentBlock.Header }}
				</DsText>

				<FormKit type="form" @submit.prevent="keyWordSearch" :actions="false">
					<FormKit type="search" v-model="keyword" prefixIcon="search" :label="vm.CurrentBlock.SubHeader"
						:placeholder="vm.CurrentBlock.SearchPlaceHolderText" @keydown.enter="keyWordSearch"
						:disabled="isFetching" data-testid="keyword-input" />
				</FormKit>
				<DocumentSearchFilter :facet-groups="facetGroups" :vm="vm" :documents-length="documents?.length"
					@update-filters="applyFilters" @reset-filters="resetFilters" />
				<DsButton class="c-document-search__opposite-link"
					v-if="vm.BaseViewModel.SearchForOppositeVersionFilesUrl" icon-end="navigate-forward" variant="link"
					:href="vm.BaseViewModel.SearchForOppositeVersionFilesUrl">{{
						vm.CurrentBlock.SearchForOppositeVersionFilesLinkLabel
					}}
				</DsButton>
			</div>
		</section>
		<DocumentSearchResult :vm="vm" :documents="documents" :is-loaded="isLoaded" :search-archived="searchArchived" />
	</div>
</template>

<script setup lang="ts">
import { DsText, DsButton } from '@coloplast/design-system';
import { onMounted } from 'vue';
import DocumentSearchFilter from './components/DocumentSearchFilter.vue';
import DocumentSearchResult from './components/DocumentSearchResult.vue';
import { FormKit } from '@formkit/vue';
import type {
	ViewModel,
} from './DocumentSearch.types';
import { FACET_KEYS } from './DocumentSearch.types';
import { useDocumentSearch } from './composables/useDocumentSearch';
import { useFacetUrlSync } from './composables/useFacetUrlSync';

const props = withDefaults(
	defineProps<{
		vm: ViewModel;
		docType?: string;
		searchArchived?: boolean;
	}>(),
	{
		searchArchived: false,
	}
);

const {
	keyword, filters, facetGroups, documents, isLoaded, isFetching,
	fetchData, applyFilters, resetFilters,
} = useDocumentSearch(props.vm, props.searchArchived!);

const { initFromUrl, serializeToURL, watchFiltersEffect } = useFacetUrlSync({
	filters,
	keyword,
	searchArchived: props.searchArchived!,
	facetKeys: FACET_KEYS,
	onChangeFromPopstate: fetchData,
});

watchFiltersEffect(fetchData, { flush: 'sync' });

onMounted(async () => {
	initFromUrl();
	await fetchData();
	serializeToURL({ push: false });
});

async function keyWordSearch(e: Event) {
	e.preventDefault();
	if (keyword.value) {
		await fetchData();
		serializeToURL({ push: true });
	}
}
</script>

<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-document-search {
	inline-size: 100%;

	&__form {
		background-color: ds-color('bg', 'neutral-subtle');
		padding: ds-space(10) ds-space(7) ds-space(8) ds-space(7);

		.c-search {
			margin-block-end: 0;

			&__input {
				padding-inline-start: ds-space(10);
			}
		}

		.doc-search-filter {
			margin-block-start: ds-space(6);
		}

		.facet__list__deselect__wrapper {
			margin: 0;
			padding: 0;
			border: none;
			gap: ds-gap(4);
		}

		.facet__list__deselect {
			padding-block-start: ds-space(1);
			margin: 0;
		}

		.facet__list__deselect__close {
			transform: translateY(-1px);
		}
	}

	&__opposite-link {
		margin-block-start: ds-space(4);
	}

	&__result {
		background-color: ds-color('bg', 'default');
		padding: t-rem(40px 20px);
		inline-size: 100%;
		overflow-x: auto;
		overflow-y: hidden;

		&__no-result-text {
			color: ds-color('fg', 'danger');
		}

		table {
			inline-size: 100%;

			tr {
				&> :first-child {
					padding-inline: 0;
				}
			}

			th,
			td {
				vertical-align: top;
				padding: t-rem(10px);

				@include t-respond-to-min(medium) {
					padding: t-rem(20px);
				}
			}

			thead th,
			thead td {
				@include t-respond-to-min(medium) {
					border-block: 1px solid ds-color('border', 'muted');
					padding-block: t-rem(10px);
				}
			}

			tbody td {
				border-block-end: 1px solid ds-color('border', 'muted');
			}
		}

		&__pdf-icon {
			display: inline-block;
			background-image: svg-load('icons/pdf.svg');
			background-repeat: no-repeat;
			background-size: contain;
			inline-size: 24px;
			block-size: 28px;
			text-indent: -999px;
			overflow: hidden;
		}
	}

	&__inner {
		inline-size: 100%;
		max-inline-size: 1008px;
		margin-inline: auto;
	}

	.sr-only {
		clip: rect(0 0 0 0);
		clip-path: inset(50%);
		block-size: 1px;
		inline-size: 1px;
		overflow: hidden;
		position: absolute;
		white-space: nowrap;
	}
}
</style>
